import { z } from 'zod';

const MAX_REDIRECTS = 3;
const REDIRECT_WINDOW_MS = 30_000;
const STORAGE_KEY = 'auth_redirect_attempts';

const RedirectAttemptsSchema = z.object({
  count: z.number().int().nonnegative(),
  windowStartMs: z.number().int().nonnegative(),
});

type RedirectAttempts = z.infer<typeof RedirectAttemptsSchema>;

const freshAttempts = (): RedirectAttempts => ({
  count: 0,
  windowStartMs: Date.now(),
});

const readAttempts = (): RedirectAttempts => {
  const raw = sessionStorage.getItem(STORAGE_KEY);
  if (raw === null) {
    return freshAttempts();
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (err) {
    console.error(
      '[RedirectLoopGuard] Corrupt sessionStorage value, resetting:',
      err,
    );
    sessionStorage.removeItem(STORAGE_KEY);
    return freshAttempts();
  }

  const result = RedirectAttemptsSchema.safeParse(parsed);
  if (!result.success) {
    console.error(
      '[RedirectLoopGuard] Invalid sessionStorage schema, resetting:',
      result.error,
    );
    sessionStorage.removeItem(STORAGE_KEY);
    return freshAttempts();
  }

  return result.data;
};

const writeAttempts = (attempts: RedirectAttempts): void => {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(attempts));
};

export interface IRedirectLoopGuard {
  shouldRedirect(): boolean;
  registerRedirect(): void;
  clearRedirects(): void;
}

export class RedirectLoopGuard implements IRedirectLoopGuard {
  shouldRedirect(): boolean {
    const attempts = readAttempts();
    if (Date.now() - attempts.windowStartMs > REDIRECT_WINDOW_MS) {
      return true;
    }
    return attempts.count < MAX_REDIRECTS;
  }

  registerRedirect(): void {
    const attempts = readAttempts();
    const now = Date.now();
    if (now - attempts.windowStartMs > REDIRECT_WINDOW_MS) {
      writeAttempts({ count: 1, windowStartMs: now });
    } else {
      writeAttempts({
        count: attempts.count + 1,
        windowStartMs: attempts.windowStartMs,
      });
    }
  }

  clearRedirects(): void {
    sessionStorage.removeItem(STORAGE_KEY);
  }
}

export const redirectLoopGuard: IRedirectLoopGuard = new RedirectLoopGuard();
